import { useMutation } from "@apollo/client";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  createStyles,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Select,
  SimpleGrid,
  Table,
  Text,
  TextInput,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons";
import axios from "axios";
import { customLoader } from "components/utilities/loader";
import { Fragment, useEffect, useState } from "react";
import { Plus, Search, Trash, X } from "tabler-icons-react";

import { DEL_PRODUCT_SKU } from "apollo/mutuations";
import ProductSkuAddModal from "components/ProductSku/ProductSkuAddModal";
import ProductSkuEditModal from "components/ProductSku/ProductSkuEditModal";
import Controls from "components/controls/Controls";
import { API, formatNumber, PAGE_SIZE_OPTIONS } from "utiles/url";

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
  },

  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },

  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },

  searchContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  searchInput: {
    flexGrow: 1,
    paddingRight: "50px", // Add padding to avoid text overlap with button
  },
  searchButton: {
    position: "absolute",
    right: 0,
    borderRadius: "0 4px 4px 0",
    height: "70%",
    width: "40px", // Fixed width for the button
    backgroundColor: "#FF6A00",
    color: "#FFFFFF",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "14px",
    cursor: "pointer",
  },
}));

function Th({ children, sortable, sorted, reversed, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th className={classes.th}>
      <UnstyledButton
        onClick={sortable ? onSort : null}
        className={classes.control}
      >
        <Group position="apart">
          <Text weight={500} size="sm">
            {children}
          </Text>
          {sortable && (
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          )}
        </Group>
      </UnstyledButton>
    </th>
  );
}

const Drivers = () => {
  const [size, setSize] = useState("10");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const { classes } = useStyles();
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const [editRow, setEditRow] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [search, setSearch] = useState("");
  const [sortedData, setSortedData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [openedDetail, setOpenedDetail] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);

  useEffect(() => {
    fetchData(activePage, size);
  }, [activePage]);

  const fetchData = async (page, pageSize = size) => {
    setLoading(true);
    try {
      let token = localStorage.getItem("auth_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(
        `${API}/product-skus?page=${page}&first=${pageSize}`,
        config,
      );
      if (response.data) {
        setLoading(false);
        setDrivers(response.data.data);
        setSortedData(response.data.data); // Ensure sorting is applied when data is fetched
        setTotal(response.data?.links);
        setTotalPages(response.data.last_page);
        setTotalRecords(
          response.data.total ?? response.data.last_page * parseInt(pageSize),
        );
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const handleSearchChange = async (event) => {
    setLoading(true);
    try {
      let token = localStorage.getItem("auth_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(
        `${API}/product-skus?search=${search}&page=${activePage}&first=${size}`,
        config,
      );
      if (response.data) {
        setDrivers(response.data.data);
        setSortedData(response.data.data);
        setTotal(response.data?.links);
        setTotalPages(response.data.last_page);
        setTotalRecords(
          response.data.total ?? response.data.last_page * parseInt(size),
        );
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const sortData = (data, payload) => {
    if (!payload.sortBy) {
      return filterData(data, payload.search);
    }

    return filterData(
      [...data].sort((a, b) => {
        if (payload.reversed) {
          return b[payload.sortBy].localeCompare(a[payload.sortBy]);
        }

        return a[payload.sortBy].localeCompare(b[payload.sortBy]);
      }),
      payload.search,
    );
  };

  const handleChange = (page) => {
    setActivePage(page);
  };

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
    fetchData(1, newSize);
  };

  const handleSort = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(drivers, { sortBy: field, reversed, search }));
  };

  const filterData = (data, search) => {
    const query = search.toLowerCase().trim();
    return data.filter((item) =>
      Object.keys(item).some(
        (key) =>
          typeof item[key] === "string" &&
          item[key] &&
          item[key].toLowerCase().includes(query),
      ),
    );
  };
  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };
  const [delProductSku, { loading: delLoading }] = useMutation(DEL_PRODUCT_SKU);

  const deleteSku = () => {
    delProductSku({
      variables: { id: deleteID },
      onCompleted(data) {
        fetchData(activePage);
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Product variant Deleted!",
        });
      },
      onError(data) {
        setOpenedDelete(false);
        showNotification({
          color: "red",
          title: "Error",
          message: "Product variant Not Deleted!",
        });
      },
    });
  };
  const handleEdit = (id) => {
    setOpenedEdit(true);
    setEditId(id);
  };

  const theme = useMantineTheme();
  const rows = sortedData?.map((row) => (
    <Fragment key={row.id}>
      <tr>
        <td>{row.product?.name.en}</td>
        <td>{row.product?.category?.name}</td>
        <td>{row.sku}</td>
        <td>{row.variants?.length}</td>
        <td>{row.order_items_count}</td>
        <td>{row.buy_price}</td>
        <td>
          {row.is_active === 1 ? (
            <Badge variant="light" color="green">
              Active
            </Badge>
          ) : (
            <Badge variant="light" color="red">
              Not Active
            </Badge>
          )}
        </td>
        <td>
          <Controls.ActionButton
            color="primary"
            title="Update"
            onClick={() => handleEdit(`${row.id}`)}
          >
            <EditIcon style={{ fontSize: "1rem" }} />
          </Controls.ActionButton>

          <Controls.ActionButton
            color="primary"
            title="Delete"
            onClick={() => handleDelete(`${row.id}`)}
          >
            <Trash size={17} />
          </Controls.ActionButton>
        </td>
      </tr>
    </Fragment>
  ));

  return loading ? (
    <LoadingOverlay
      visible={loading || delLoading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={openedEdit}
        onClose={() => setOpenedEdit(false)}
        title="Editing Product Variant"
        padding="xl"
        size="60%"
        position="bottom"
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
      >
        <ProductSkuEditModal
          setOpenedEdit={setOpenedEdit}
          editId={editId}
          fetchData={fetchData}
          activePage={activePage}
        />
      </Drawer>

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        title="Adding Product Variant"
        padding="xl"
        size="60%"
        position="bottom"
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
      >
        <ProductSkuAddModal
          total={total}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpened={setOpened}
          fetchData={fetchData}
          totalPages={totalPages}
        />
      </Drawer>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <SimpleGrid cols={3}>
            <div>
              <Button
                onClick={() => setOpened(true)}
                style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
                leftIcon={<Plus size={14} />}
              >
                Add Product Variant
              </Button>
            </div>
            <div></div>

            <div className={classes.searchContainer}>
              <TextInput
                placeholder="Search"
                mb="md"
                icon={<Search size={14} />}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                className={classes.searchInput}
                rightSection={
                  search && (
                    <UnstyledButton
                      onClick={() => {
                        setSearch("");
                        fetchData(activePage);
                      }}
                      style={{ padding: 5 }}
                    >
                      <X size={16} color="red" />
                    </UnstyledButton>
                  )
                }
              />
              <button
                className={classes.searchButton}
                onClick={handleSearchChange}
              >
                <Search size={16} />
              </button>
            </div>
          </SimpleGrid>
          <Table
            highlightOnHover
            horizontalSpacing="md"
            verticalSpacing="xs"
            sx={{ tableLayout: "fixed", minWidth: 700 }}
          >
            <thead>
              <tr>
                <Th sortable={false} onSort={() => handleSort("phone")}>
                  Product Name
                </Th>
                <Th sortable={false} onSort={() => handleSort("email")}>
                  Category
                </Th>
                <Th sortable={false} onSort={() => handleSort("name")}>
                  SKU
                </Th>
                <Th sortable={false} onSort={() => handleSort("email")}>
                  Variant
                </Th>
                <Th sortable={false} onSort={() => handleSort("phone")}>
                  Order Count
                </Th>
                <Th sortable={false} onSort={() => handleSort("email")}>
                  Sell Price
                </Th>
                <Th sortable={false} onSort={() => handleSort("phone")}>
                  Status
                </Th>
                <Th sortable={false}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows?.length > 0 ? (
                rows
              ) : (
                <tr>
                  <td colSpan={8}>
                    <Text weight={500} align="center">
                      Nothing found
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "12px",
              padding: "6px 4px",
              borderTop: "1px solid #e9ecef",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {/* Left: Total Records */}
            <Text size="sm" style={{ color: "#FF6A00" }}>
              {`Total Records: ${formatNumber(totalRecords)}`}
            </Text>

            {/* Right: Rows select + navigation */}
            <Group spacing={6} noWrap>
              <Text size="sm" color="dimmed">
                Rows
              </Text>
              <Select
                value={size}
                onChange={handlePageSizeChange}
                data={PAGE_SIZE_OPTIONS}
                style={{ width: 72 }}
                size="xs"
              />
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(1)}
                disabled={activePage <= 1}
                title="First page"
                style={{ fontWeight: 700 }}
              >
                «
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(activePage - 1)}
                disabled={activePage <= 1}
                title="Previous page"
              >
                ‹
              </ActionIcon>
              <Text size="sm" style={{ minWidth: 40, textAlign: "center" }}>
                {activePage} / {totalPages}
              </Text>
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(activePage + 1)}
                disabled={activePage >= totalPages}
                title="Next page"
              >
                ›
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(totalPages)}
                disabled={activePage >= totalPages}
                title="Last page"
                style={{ fontWeight: 700 }}
              >
                »
              </ActionIcon>
            </Group>
          </div>
        </ScrollArea>
        <Modal
          opened={openedDelete}
          onClose={() => setOpenedDelete(false)}
          title="Warning"
          centered
        >
          <p>Are you sure do you want todelete this Product variant?</p>
          <Group position="right">
            <Button onClick={() => deleteSku()} color="red">
              Delete
            </Button>
          </Group>
        </Modal>
      </Card>
    </div>
  );
};

export default Drivers;
